import { formatDateOnlyMMDDYYYY, toDateOnly } from './dateOnly';
// /src/utils/stringUtils.js

/**
 * Converts a string to title case.
 * @param {string} str The string to convert.
 * @returns {string} The title-cased string.
 */
export function toTitleCase(str) {
  if (!str) return '';
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// A person's name as it is saved: title-cased, trimmed, and with runs of
// spaces made one. "  ben   blocker " was saved as "Ben   Blocker", and a
// search for "ben blocker" then missed him (QA 2026-10-07). toTitleCase keeps
// its spacing on purpose: it also runs on special-request text, where
// newlines matter.
export const normalizePersonName = (value) =>
  toTitleCase(String(value ?? '').replace(/\s+/g, ' ').trim());

/**
 * Normalizes a phone number to (xxx) xxx-xxxx format.
 * @param {string} phone The phone number string to normalize.
 * @returns {string} The normalized phone number or the original string if it cannot be normalized.
 */
export function normalizePhoneNumber(phone) {
  if (!phone) return '';
  const cleaned = ('' + phone).replace(/\D/g, ''); // Remove all non-digit characters
  // +1 555 123 4567 is the same number as (555) 123-4567: drop the country code.
  const national = cleaned.length === 11 && cleaned.startsWith('1') ? cleaned.slice(1) : cleaned;
  const match = national.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone; // Return original if not a 10-digit number
}

// US phone (MealOps is US-only): 10 digits, or 11 with a leading 1, ignoring
// formatting like (555) 123-4567 / 555.123.4567 / +1 555 123 4567, with an
// area code that starts 2-9 (no US area code starts with 0 or 1, so Twilio
// refuses those; "(111) 555-0120" was saved with SMS on, 2026-10-07). The
// one rule for every phone a coach types (checkout, the roster, team setup,
// the profile). Migration 096's is_valid_us_phone is the same rule in the
// database minus the area-code check, so the database stays a backstop for
// the shape only. A phone is optional; one that is there must pass this.
export const isValidPhoneNumber = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return false;
  // Reject stray letters/symbols beyond common phone punctuation.
  if (/[^\d\s()+.\-]/.test(raw)) return false;
  const digits = raw.replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return national.length === 10 && /^[2-9]/.test(national);
};

// +15551234567 for a number that passes isValidPhoneNumber, else null.
export const toE164PhoneNumber = (value) => {
  if (!isValidPhoneNumber(value)) return null;
  const digits = String(value).replace(/\D/g, '');
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
};

// A real calendar day, as yyyy-MM-dd — or '' when the parts do not make one.
// "2001-13-45" has the right SHAPE, and the database refuses it: a roster
// import carrying one lost the whole batch (2026-09-21). Years outside a
// plausible birthday are refused too; a typo like 0203 is not a birth year.
const toValidIsoDate = (y, m, d) => {
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);
  if (![year, month, day].every(Number.isInteger)) return '';
  if (year < 1900 || year > new Date().getFullYear()) return '';
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return '';
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

export function normalizeBirthday(dateStr) {
  if (!dateStr) return '';

  const s = String(dateStr).trim();

  // YYYY-MM-DD, YYYY/MM/DD or YYYY-M-D
  if (/^\d{4}[\/-]\d{1,2}[\/-]\d{1,2}$/.test(s)) {
    const [y, m, d] = s.split(/[\/-]/);
    return toValidIsoDate(y, m, d);
  }

  // MM/DD/YYYY or M-D-YYYY
  if (/^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/.test(s)) {
    const [m, d, y] = s.split(/[\/-]/);
    return toValidIsoDate(y, m, d);
  }

  // Fallback: try to parse safely (use UTC to avoid TZ shifts)
  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) {
    return toValidIsoDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
  }

  // Unknown format -> empty string so the <input type="date"> stays valid
  return '';
}

// min/max for a birthday <input type="date">, so the picker offers only days
// normalizeBirthday keeps. Today is the coach's local calendar day.
export const birthdayInputBounds = (now = new Date()) => ({ min: '1900-01-01', max: toDateOnly(now) });

// A birthday field holds what the date input reported, untouched, while the
// coach types: Chrome reports each year digit as it lands (0001-09-03, then
// 0019-09-03...), and normalizing that on every keystroke blanked the field
// after the first digit (QA 2026-10-07). It is judged here, on save: true
// when something was entered and it is not a real birthday, or is after
// today on the coach's calendar (the roster forms' copy and the date input's
// max both say "today"; normalizeBirthday alone let a date later this year
// through). Edit Profile checks its birthday with this too.
export const isUnrealBirthday = (value, now = new Date()) => {
  const raw = String(value ?? '').trim();
  if (!raw) return false;
  const day = normalizeBirthday(raw);
  return !day || day > toDateOnly(now);
};

// name@host.tld, nothing fancier: enough to catch "bademail" before it is
// stored as a member's address (the same rule checkout applies).
export const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? '').trim());

/**
 * Formats a date string to MM/DD/YYYY.
 * @param {string} dateStr The date string to format.
 * @returns {string} The formatted date string (MM/DD/YYYY) or an empty string if invalid.
 */
export function formatDateToMMDDYYYY(dateStr) {
  // Date-only input (birthday); read the calendar day, never a zone-shifted instant.
  return formatDateOnlyMMDDYYYY(String(dateStr ?? '').slice(0, 10));
}